/**
 * DraftDiffViewer — line-by-line side-by-side compare between two
 * iteration content strings.
 *
 * Uses a tiny LCS-based diff so we don't pull in react-diff-viewer (and
 * its ~80 KB dependency tree). Output is monochrome chrome with status
 * colours reserved for insertion (success) / deletion (destructive) /
 * change (warning). Pure component — given the two strings it always
 * renders the same thing, easy to snapshot test.
 */
import * as React from 'react';

import { cn } from '../../lib/utils';

interface DraftDiffViewerProps {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
}

type DiffOp = 'eq' | 'add' | 'del';

interface DiffLine {
  op: DiffOp;
  text: string;
}

export function DraftDiffViewer({
  before,
  after,
  beforeLabel = 'Previous',
  afterLabel = 'Updated',
  className,
}: DraftDiffViewerProps) {
  const diff = React.useMemo(
    () => computeLineDiff(before.split('\n'), after.split('\n')),
    [before, after],
  );
  const stats = React.useMemo(() => summarise(diff), [diff]);

  return (
    <div
      className={cn('flex flex-col gap-2 rounded border border-border bg-background', className)}
    >
      <header className="flex items-center justify-between border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        <span>
          {beforeLabel} → {afterLabel}
        </span>
        <span className="flex items-center gap-2 font-mono text-[11px] normal-case tracking-normal">
          <span className="text-success">+{stats.additions}</span>
          <span className="text-destructive">−{stats.deletions}</span>
        </span>
      </header>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-b bg-border">
        <DiffPane title={beforeLabel} side="left" diff={diff} />
        <DiffPane title={afterLabel} side="right" diff={diff} />
      </div>
    </div>
  );
}

function DiffPane({
  title,
  side,
  diff,
}: {
  title: string;
  side: 'left' | 'right';
  diff: DiffLine[];
}) {
  return (
    <div className="flex flex-col bg-background">
      <span className="sr-only">{title}</span>
      <pre className="m-0 max-h-[420px] overflow-auto p-3 font-mono text-[12px] leading-[1.55]">
        {diff.map((line, idx) => renderLine(line, side, idx))}
      </pre>
    </div>
  );
}

function renderLine(line: DiffLine, side: 'left' | 'right', idx: number) {
  if (line.op === 'eq') {
    return (
      <div key={`${side}-${idx}`} className="px-1 text-foreground/80">
        <span aria-hidden className="select-none pr-2 text-muted-foreground/60">
          {'  '}
        </span>
        {line.text || ' '}
      </div>
    );
  }
  if (line.op === 'add') {
    if (side === 'left') {
      // Empty slot on the "before" pane.
      return (
        <div key={`${side}-${idx}`} className="px-1 text-muted-foreground/40">
          <span aria-hidden className="select-none pr-2">
            {'  '}
          </span>
          {' '}
        </div>
      );
    }
    return (
      <div key={`${side}-${idx}`} className="bg-success/10 px-1 text-foreground">
        <span aria-hidden className="select-none pr-2 text-success">
          +{' '}
        </span>
        {line.text || ' '}
      </div>
    );
  }
  // del
  if (side === 'right') {
    return (
      <div key={`${side}-${idx}`} className="px-1 text-muted-foreground/40">
        <span aria-hidden className="select-none pr-2">
          {'  '}
        </span>
        {' '}
      </div>
    );
  }
  return (
    <div key={`${side}-${idx}`} className="bg-destructive/10 px-1 text-foreground">
      <span aria-hidden className="select-none pr-2 text-destructive">
        −{' '}
      </span>
      {line.text || ' '}
    </div>
  );
}

function summarise(diff: DiffLine[]) {
  let additions = 0;
  let deletions = 0;
  for (const d of diff) {
    if (d.op === 'add') additions += 1;
    if (d.op === 'del') deletions += 1;
  }
  return { additions, deletions };
}

/**
 * Compute a per-line diff using the Myers-ish LCS algorithm. Returns a
 * sequence where each entry is either "eq" (line unchanged), "add" (only
 * in "after"), or "del" (only in "before"). Sufficient for the common
 * prompt-edit case in training sessions; not optimal for very large
 * files (>5000 lines) but fine for prompt templates.
 */
export function computeLineDiff(left: string[], right: string[]): DiffLine[] {
  const m = left.length;
  const n = right.length;
  // LCS DP table.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (left[i] === right[j]) {
        // biome-ignore lint/style/noNonNullAssertion: dp matrix is dense and pre-initialised
        dp[i]![j] = dp[i + 1]![j + 1]! + 1;
      } else {
        // biome-ignore lint/style/noNonNullAssertion: dp matrix is dense and pre-initialised
        dp[i]![j] = Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
      }
    }
  }
  // Backtrack.
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (left[i] === right[j]) {
      out.push({ op: 'eq', text: left[i] ?? '' });
      i += 1;
      j += 1;
      continue;
    }
    if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      out.push({ op: 'del', text: left[i] ?? '' });
      i += 1;
    } else {
      out.push({ op: 'add', text: right[j] ?? '' });
      j += 1;
    }
  }
  while (i < m) {
    out.push({ op: 'del', text: left[i] ?? '' });
    i += 1;
  }
  while (j < n) {
    out.push({ op: 'add', text: right[j] ?? '' });
    j += 1;
  }
  return out;
}
